import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import type { Participant } from "../types";
import { mentionTrigger } from "../lib/mentions";

export function useMentions({
  text,
  participants,
  selected,
  setText,
  setSelected,
  input,
}: {
  text: string;
  participants: Participant[];
  selected: string[];
  setText: (text: string) => void;
  setSelected: (ids: string[]) => void;
  input: RefObject<HTMLTextAreaElement | null>;
}) {
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const selection = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (selection.current === null) return;
    input.current?.focus();
    input.current?.setSelectionRange(selection.current, selection.current);
    selection.current = null;
  }, [text, input]);
  const trigger = mentionTrigger(text, caret);
  const token = trigger ? `${trigger.start}:${trigger.query}` : null;
  const matches =
    trigger && token !== dismissed
      ? participants.filter((participant) =>
          participant.name
            .toLocaleLowerCase()
            .includes(trigger.query.toLocaleLowerCase()),
        )
      : [];
  const index = Math.min(highlight, Math.max(0, matches.length - 1));
  const choose = (participant: Participant) => {
    if (!trigger) return;
    const inserted = `@${participant.name} `;
    const next =
      text.slice(0, trigger.start) + inserted + text.slice(trigger.end);
    if (next.length > 4000) return;
    selection.current = trigger.start + inserted.length;
    setText(next);
    setSelected([...new Set([...selected, participant.memberId])]);
    setCaret(trigger.start + inserted.length);
    setHighlight(0);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      !matches.length ||
      event.nativeEvent.isComposing ||
      event.keyCode === 229
    )
      return false;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight(
        (index + (event.key === "ArrowDown" ? 1 : matches.length - 1)) %
          matches.length,
      );
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      choose(matches[index]);
      return true;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setDismissed(token);
      return true;
    }
    return false;
  };
  return {
    matches,
    index,
    choose,
    onKeyDown,
    setCaret: (position: number) => {
      setCaret(position);
      setDismissed(null);
      setHighlight(0);
    },
  };
}
